import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  TaxProcedureProfileService,
} from "subModule/src/core/services";
import {
  NewPurchaseOrderResponse,
  PurchaseOrderResponse,
  SaveInvoice,
  SaveInvoiceResponse,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { DeleteData } from "subModule/src/type";
import { useGetServicesDropdownService } from "subModule/src/services/service/useService";
import { InventoryServicesTable } from "components/Inventory/InventoryServicesTable";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import DynamicSelect from "common/Select/Select";
import {
  useGetPurchaseOrderByIdService,
  useGetPurchaseOrdersTypeService,
} from "subModule/src/services/inventory/useInventory";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import FormDialog from "common/Modal/Modal";
import ServicesModal from "pages/Service/ServicesModal";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { GetItemsDropdownResponse } from "@subModule/src/services/items/interface";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import { GetDefaultTaxProcedureResponse } from "subModule/src/services/taxProcedure/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

const PurchaseInvoiceCreateUpdate: React.FC = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "bills",
    moduleHref: "/inventory/supplier-bills",
    entityFallbackLabelKey: "supplierBill",
    isEdit: !!params?.id,
    action: "details",
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      purchaseInvoiceItem: [],
      purchaseInvoiceService: [],
      invoiceDate: dayjs(new Date())?.format("YYYY-MM-DD"),
      taxable: false,
      taxProcedureId: null,
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: watch("storeId")?.value,
      search: dropdownSearchTerm,
    },
  );
  const { data: servicesData, refetch: servicesRefetch } =
    useGetServicesDropdownService({
      pageNumber: dropdownPage,
      size: 20,
      search: dropdownSearchTerm,
    });
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: dropdownPage,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: poData, refetch: poRefetch } = useGetPurchaseOrdersTypeService({
    pageNumber: dropdownPage,
    size: 50,
    type: 1,
    poNumber: dropdownSearchTerm,
  });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { refetch: poByIdFetch } = useGetPurchaseOrderByIdService(
    watch("poId")?.value,
  );
  const [purchaseInvoiceItem, setPurchaseInvoiceItem] = useState<any[]>([]);
  const [purchaseInvoiceService, setPurchaseInvoiceService] = useState<any[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [invoice, setInvoice] = useState<SaveInvoice>();
  const [addNew, setAddNew] = useState<string>("");
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  useEffect(() => {
    vendorsRefetch();
    storesRefetch();
    servicesRefetch();
    poRefetch();
    taxProceduresRefetch();
    if (params?.id) {
      if (loading) getInvoiceById();
    }
  }, []);

  const getDefaultTaxProcedure = () => {
    TaxProcedureProfileService.getActiveDefaultTaxProcedure(3)
      .then((res: GetDefaultTaxProcedureResponse) => {
        if (res.success && res.data?.id) {
          setValue("taxProcedureId", {
            value: res.data.id,
            label: res.data.title,
            percentageValue: res.data.percentageValue,
          });
          setTaxPercentage((res.data.percentageValue || 0) / 100);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  useEffect(() => {
    if (watch("storeId")?.value) {
      setLoading(true);
      getParts();
    }
  }, [watch("storeId")?.value]);
  const getParts = () => {
    itemsRefetch()
      .then((res: GetItemsDropdownResponse | unknown | any) => {
        if (res?.data?.pages[0]?.success) {
          ///
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const getInvoiceById = () => {
    InventoryProfileService.getInvoiceById(params?.id, 1)
      .then((res: any) => {
        if (res.success) {
          setLoading(false);
          setInvoice(res.data as any);
          captureLabel(
            res.data?.invoiceNumber != null
              ? String(res.data.invoiceNumber)
              : null,
          );
          setPurchaseInvoiceItem((res.data as any)?.purchaseInvoiceItem || []);
          setValue("purchaseInvoiceItem", res.data.purchaseInvoiceItem || []);
          setPurchaseInvoiceService(
            (res.data as any)?.purchaseInvoiceService || [],
          );
          setValue(
            "purchaseInvoiceService",
            res.data?.purchaseInvoiceService || [],
          );
          setValue("storeId", res.data?.storeId);
          setValue("id", params?.id);
          setValue("invoiceIndex", res.data?.invoiceIndex);
          setValue("invoiceNumber", res.data?.invoiceNumber);
          setValue("invoiceDate", res.data?.invoiceDate?.split(" ")[0]);
          setValue("dueDate", res.data?.dueDate?.split(" ")[0] || null);
          setValue("comments", res.data?.comments);
          setValue("supplierId", res.data?.supplierId);
          setValue("taxable", !!res.data?.taxable);
          setValue(
            "taxProcedureId",
            res.data?.taxProcedureId
              ? {
                  value: res.data.taxProcedureId,
                  label: res.data?.taxProcedureTitle || res.data.taxProcedureId,
                  percentageValue: res.data?.taxPercentageValue,
                }
              : null,
          );
          if (res.data?.taxable && res.data?.taxPercentageValue != null) {
            setTaxPercentage(Number(res.data.taxPercentageValue) / 100);
          }
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const getPOById = async () => {
    await poByIdFetch()
      .then((res: NewPurchaseOrderResponse | any) => {
        if (res?.data?.success) {
          setInvoice(res.data.data);
          setPurchaseInvoiceItem(res?.data.data?.purchaseOrderItems || []);
          setValue(
            "purchaseInvoiceItem",
            res?.data.data?.purchaseOrderItems || [],
          );
          setPurchaseInvoiceService(res?.data.data?.purchaseOrderSvc || []);
          setValue(
            "purchaseInvoiceService",
            res?.data.data?.purchaseOrderSvc || [],
          );
          setValue("storeId", {
            value: res?.data.data?.storeId,
            label: res?.data.data?.storeTitle,
          });
          setValue("supplierId", {
            value: res?.data.data?.supplierId,
            label: res?.data.data?.vendorTitle,
          });
          setValue("comments", res?.data.data?.poMemo);
          setValue("taxable", !!res?.data.data?.taxable);
          setValue(
            "taxProcedureId",
            res?.data.data?.taxProcedureId
              ? {
                  value: res.data.data.taxProcedureId,
                  label:
                    res.data.data?.taxProcedureTitle ||
                    res.data.data.taxProcedureId,
                  percentageValue: res.data.data?.taxPercentageValue,
                }
              : null,
          );
          if (
            res?.data.data?.taxable &&
            res?.data.data?.taxPercentageValue != null
          ) {
            setTaxPercentage(Number(res.data.data.taxPercentageValue) / 100);
          } else {
            setTaxPercentage(0);
          }
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const poOptions = (poData?.pages[0]?.data?.content || [])?.map(
    (item: any) => ({ value: item.id, label: item.poNumber }),
  );
  const taxProcedureOptions = (
    taxProceduresData?.pages[0]?.data?.content || []
  ).map((item: { id: number; title: string; percentageValue?: number }) => ({
    value: item.id,
    label: item.title,
    percentageValue: item.percentageValue,
  }));
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const columnsService = [
    { id: "serviceId" },
    { id: "serviceCode" },
    {
      id: "serviceName",
      label: t("service") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const DeleteItem = (row: any, typeName: string) => {
    if (typeName === "purchaseInvoiceItem" && params?.id) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "purchaseinvoiceitems",
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getInvoiceById();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    } else if (typeName === "purchaseInvoiceItem" && !params?.id) {
      setPurchaseInvoiceItem((prevRows) => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue("purchaseInvoiceItem", []);
        setTimeout(
          () =>
            setValue("purchaseInvoiceItem", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        );
        return newRows;
      });
    }
    if (typeName === "purchaseInvoiceService" && params?.id) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "purchaseinvoiceservices",
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getInvoiceById();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    } else if (typeName === "purchaseInvoiceService" && !params?.id) {
      setPurchaseInvoiceService((prevRows) => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue("purchaseInvoiceService", []);
        setTimeout(
          () =>
            setValue("purchaseInvoiceService", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        );
        return newRows;
      });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "purchaseInvoiceItem") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      if (!params?.id) (newItem as any).id = Date.now();
      setPurchaseInvoiceItem((prevRows) => [...prevRows, newItem]);
    } else {
      const newService = columnsService.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      if (!params?.id) (newService as any).id = Date.now();
      setPurchaseInvoiceService((prevRows) => [...prevRows, newService]);
    }
  };
  const handleItem = (row: any, typeName: string, action: string) => {
    if (typeName === "purchaseInvoiceItem" && params?.id) {
      if (action === "create") {
        InventoryProfileService.createPurchaseInvoiceItem({
          ...row,
          purchaseInvoiceId: invoice?.id,
          storeId: invoice?.storeId,
        })
          .then((response: SaveInvoiceResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("supplierBillItem") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getInvoiceById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getInvoiceById();
          });
      } else {
        InventoryProfileService.updatePurchaseInvoiceItem({
          ...row,
          purchaseInvoiceId: invoice?.id,
          storeId: invoice?.storeId,
        })
          .then((response: SaveInvoiceResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("supplierBillItem") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getInvoiceById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getInvoiceById();
          });
      }
    } else if (typeName === "purchaseInvoiceService" && params?.id) {
      if (action === "create") {
        InventoryProfileService.createPurchaseInvoiceService({
          ...row,
          purchaseInvoiceId: invoice?.id,
          storeId: invoice?.storeId,
        })
          .then((response: SaveInvoiceResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Service") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getInvoiceById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getInvoiceById();
          });
      } else {
        InventoryProfileService.updatePurchaseInvoiceService({
          ...row,
          purchaseInvoiceId: invoice?.id,
          storeId: invoice?.storeId,
        })
          .then((response: SaveInvoiceResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Service") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getInvoiceById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getInvoiceById();
          });
      }
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "purchaseInvoiceItem") {
      const updatedItems = [...purchaseInvoiceItem];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setPurchaseInvoiceItem(updatedItems);
    } else {
      const updatedServices = [...purchaseInvoiceService];
      updatedServices[index][columnId] = value;
      setPurchaseInvoiceService(updatedServices);
    }
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(true);
      const filteredItems = purchaseInvoiceItem.filter(
        (item: any) => item?.itemName,
      );
      const filteredServices = purchaseInvoiceService.filter(
        (service: any) => service?.serviceName,
      );
      if (filteredItems.length === 0) {
        enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
        return;
      }
      filteredItems.every(
        (row: any) => delete row.total && delete row.id && row.unitTitle,
      );
      filteredServices.every((row: any) => delete row.total && delete row.id);
      const {
        taxAmount: _omitTaxAmount,
        taxProcedureId,
        taxable,
        ...rest
      } = data;
      InventoryProfileService.saveStockInvoice({
        ...rest,
        taxable: !!taxable,
        taxProcedureId:
          taxable && taxProcedureId?.value ? taxProcedureId.value : null,
        storeId: data.storeId.value,
        supplierId: data.supplierId.value,
        poId: data.poId?.value || null,
        purchaseInvoiceItem: filteredItems,
        purchaseInvoiceService: filteredServices?.length
          ? filteredServices.map((row: any) => ({
              ...row,
              serviceId:
                typeof row.serviceId === "string" ? null : row.serviceId,
            }))
          : [],
      })
        .then((response: SaveInvoiceResponse) => {
          if (response.success) {
            InventoryProfileService.changeStatusEntity({
              typeName: "purchaseinvoice",
              entityId: response?.data?.id,
              status: 2,
            })
              .then((res: any) => {
                if (res.success) {
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={res.status}
                      text={
                        t("supplierBill") +
                        " " +
                        t("saved") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                  navigate(
                    `/inventory/supplier-bill/${response?.data?.id}/print`,
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={res?.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={
                      err?.message ||
                      err?.data?.message ||
                      "Something went wrong"
                    }
                  />,
                  { variant: "error" },
                );
              });
          } else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(false));
    }
  };
  const calculateTotals = () => {
    const itemTotals = purchaseInvoiceItem.reduce(
      (acc, item) => {
        const unitPrice = parseFloat(item.unitPrice || 0);
        const quantity = parseFloat(item.quantity || 0);
        const discountPercentage = parseFloat(item.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subTotal: acc.subTotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subTotal: 0, discount: 0, total: 0 },
    );

    const serviceTotals = purchaseInvoiceService.reduce(
      (acc, service) => {
        const unitPrice = parseFloat(service.unitPrice || 0);
        const quantity = parseFloat(service.quantity || 0);
        const discountPercentage = parseFloat(service.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subTotal: acc.subTotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subTotal: 0, discount: 0, total: 0 },
    );

    return {
      subTotal: itemTotals.subTotal + serviceTotals.subTotal,
      discount: itemTotals.discount + serviceTotals.discount,
      taxableTotal: itemTotals.total + serviceTotals.total, // Total before tax
      total: itemTotals.total + serviceTotals.total, // Final total before tax
    };
  };
  const totals = calculateTotals();
  const displayTaxAmount = params?.id
    ? (invoice as PurchaseOrderResponse)?.taxAmount || 0
    : watch("taxable")
      ? totals.taxableTotal * taxPercentage
      : 0;
  const displayTotalWithTax = params?.id
    ? (invoice as PurchaseOrderResponse)?.totalAmount || 0
    : totals.total + displayTaxAmount;
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
    table?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (table === "inventoryServices") {
        servicesRefetch().finally(() => setSearchLoading(null));
        return;
      }
      if (typeName === "stores")
        storesRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "purchaseOrders")
        poRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "taxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/supplier-bills")}
        onSubmit={() => {}}
        heading={t("bills")}
        icon={
          <InventoryIcon sx={{ fill: "#000", width: "28px", height: "28px" }} />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      <Grid
        container
        component="form"
        id="form"
        className="px-[8px] items-baseline"
        rowGap={2.5}
        columnGap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container className="items-center">
          <Grid item xs={12} sm={6} className="text-start">
            <Typography
              className="font-Saira text-[15px] font-semibold"
              style={{ color: themeColors?.primary }}
            >
              {params?.id ? t("supplierBill") : t("newSupplierBill")}{" "}
              {t("Details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} className="hide-on-print">
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || loading}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("save")}
                </Button>
              )}
              {params?.id ? (
                <>
                  {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                  <Button
                    className="secondary"
                    style={{ color: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                </>
              ) : null}
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => navigate("/inventory/supplier-bills")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>
        {params?.id ? (
          <Grid
            container
            className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
            style={{ background: themeColors?.primary }}
          >
            <Grid item xs={4}>
              <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                {t("supplierBill")}
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("supplierBill")} # </span>{" "}
                <span>{invoice?.invoiceNumber || ""}</span>
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Date")}</span>{" "}
                <span>{invoice?.invoiceDate?.split("T")[0] || ""}</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="flex justify-end gap-10">
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("Supplier")} {t("name")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">
                    {invoice?.vendorTitle || ""}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("store") + " " + t("name")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">
                    {invoice?.storeTitle || ""}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Requested")}</span> <br />
                  <span className="font-medium">
                    {invoice?.totalQuantity || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Received")}</span> <br />
                  <span className="font-medium">
                    {invoice?.totalReceivedQuantity || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Subtotal")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (invoice as PurchaseOrderResponse)?.subtotalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                {/* Tax amount from BE — display only */}
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("tax")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (invoice as PurchaseOrderResponse)?.taxAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Discount")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (invoice as PurchaseOrderResponse)?.discount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Total")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (invoice as PurchaseOrderResponse)?.totalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
              </div>
            </Grid>
          </Grid>
        ) : null}
        <Grid
          container
          item
          xs={12}
          gap={2.5}
          className="stockTransferDetails flex-nowrap"
        >
          {params?.id ? null : (
            <Grid item xs={12} md={4} lg={3}>
              <Controller
                name="poId"
                control={control}
                defaultValue={null}
                rules={{ required: false }}
                render={({ field }) => (
                  <DynamicSelect
                    label="Purchase Orders"
                    defaultPlaceholder="phSelectPurchaseOrder"
                    {...field}
                    options={poOptions}
                    isSearchable
                    isRequired={false}
                    isClearable
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      reset({ poId: selectedOption }, { keepValues: true });
                      setPurchaseInvoiceItem([]);
                      setPurchaseInvoiceService([]);
                      if (selectedOption?.value)
                        setTimeout(() => getPOById(), 300);
                      if (!selectedOption?.value) {
                        reset();
                        setInvoice({});
                      }
                    }}
                    error={errors.poId?.message as string}
                    isLoading={searchLoading === "purchaseOrders"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        handleDropdownActions(
                          "purchaseOrders",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (dropdownSearchTerm || dropdownPage > 1)
                          handleDropdownActions(
                            "purchaseOrders",
                            "searchIt",
                            "",
                          );
                        else setDropdownSearchTerm("");
                        setDropdownPage(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(poData?.pages[0]?.data as any).last)
                        handleDropdownActions("purchaseOrders", "loadNext");
                    }}
                    page={dropdownPage}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="invoiceDate"
              control={control}
              rules={{
                validate: (value) => {
                  const date = new Date(value);
                  return isNaN(date.getTime()) ? t("Invalid Date") : true;
                },
              }}
              render={({ field }) => (
                <BasicDatePicker
                  type="datePicker"
                  {...field}
                  label={t("supplierBill") + " " + t("Date")}
                  fullWidth
                  onChange={(value: any) =>
                    field.onChange(
                      new Date(value)
                        .toLocaleDateString("en-CA", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                        ?.split("/")
                        .reverse()
                        .join("-"),
                    )
                  }
                  required={true}
                  error={errors.invoiceDate?.message as string}
                  disable={params?.id}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="dueDate"
              control={control}
              rules={{
                validate: (value) => {
                  const date = new Date(value);
                  return isNaN(date.getTime()) && value
                    ? t("Invalid Date")
                    : true;
                },
              }}
              render={({ field }) => (
                <BasicDatePicker
                  type="datePicker"
                  {...field}
                  label={t("Due") + " " + t("Date")}
                  fullWidth
                  onChange={(value: any) =>
                    field.onChange(
                      new Date(value)
                        .toLocaleDateString("en-CA", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                        ?.split("/")
                        .reverse()
                        .join("-"),
                    )
                  }
                  required={false}
                  error={errors.dueDate?.message as string}
                  disable={params?.id}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="supplierId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("vendors")}
                  defaultPlaceholder="phSelectSupplier"
                  {...field}
                  options={
                    params?.id
                      ? [
                          {
                            value: invoice?.supplierId,
                            label: invoice?.vendorTitle,
                          },
                        ]
                      : supplierOptions
                  }
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                  }}
                  error={errors.supplierId?.message as string}
                  value={
                    params?.id
                      ? {
                          value: invoice?.supplierId,
                          label: invoice?.vendorTitle,
                        }
                      : watch("supplierId")
                  }
                  isDisabled={params?.id ? true : false}
                  isLoading={searchLoading === "suppliers"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("suppliers", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("suppliers", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(vendorsData?.pages[0]?.data as any).last)
                      handleDropdownActions("suppliers", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="storeId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("stores")}
                  defaultPlaceholder="phSelectWarehouse"
                  {...field}
                  options={
                    params?.id
                      ? [
                          {
                            value: invoice?.storeId,
                            label: invoice?.storeTitle,
                          },
                        ]
                      : storeOptions
                  }
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    setPurchaseInvoiceItem([]);
                    setValue("purchaseInvoiceItem", []);
                  }}
                  error={errors.storeId?.message as string}
                  value={
                    params?.id
                      ? { value: invoice?.storeId, label: invoice?.storeTitle }
                      : watch("storeId")
                  }
                  isDisabled={params?.id ? true : false}
                  isLoading={searchLoading === "stores"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("stores", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("stores", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(storesData?.pages[0]?.data as any).last)
                      handleDropdownActions("stores", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <TextField
              {...register("referenceNumber", {
                validate: {
                  required: (value) =>
                    value?.trim() !== "" || t("This field is required."),
                },
              })}
              placeholder={t("phBillReferenceNo")}
              label={t("referenceNo") + " *"}
              title={t("referenceNo")}
              fullWidth
              error={!!errors.referenceNumber}
              helperText={errors.referenceNumber?.message as string}
              InputLabelProps={{ shrink: true }}
              disabled={params?.id ? true : false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            style={{ marginTop: params?.id ? 0 : "14px" }}
          >
            <Controller
              name="taxable"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <CommonCheckbox
                  {...field}
                  label={t("taxable")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.checked);
                    if (!event.target.checked) {
                      setValue("taxProcedureId", null);
                      setTaxPercentage(0);
                    } else if (!params?.id) {
                      getDefaultTaxProcedure();
                    }
                  }}
                  checked={!!field.value}
                  color={themeColors?.primary}
                  disabled={params?.id ? true : false}
                />
              )}
            />
          </Grid>
          {watch("taxable") ? (
            <Grid item xs={12} md={4} lg={3}>
              <Controller
                name="taxProcedureId"
                control={control}
                defaultValue={null}
                rules={{
                  required: watch("taxable")
                    ? t("This field is required.")
                    : false,
                }}
                render={({ field }) => (
                  <DynamicSelect
                    label={t("taxProcedure")}
                    defaultPlaceholder="phSelectTaxProcedure"
                    {...field}
                    options={
                      params?.id && watch("taxProcedureId")
                        ? [watch("taxProcedureId")]
                        : taxProcedureOptions
                    }
                    isSearchable
                    isClearable
                    isRequired
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTaxPercentage(
                        selectedOption?.percentageValue
                          ? selectedOption.percentageValue / 100
                          : 0,
                      );
                    }}
                    error={errors.taxProcedureId?.message as string}
                    isDisabled={params?.id ? true : false}
                    isLoading={searchLoading === "taxProcedures"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        handleDropdownActions(
                          "taxProcedures",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (dropdownSearchTerm || dropdownPage > 1)
                          handleDropdownActions(
                            "taxProcedures",
                            "searchIt",
                            "",
                          );
                        else setDropdownSearchTerm("");
                        setDropdownPage(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(taxProceduresData?.pages[0]?.data as any).last)
                        handleDropdownActions("taxProcedures", "loadNext");
                    }}
                    page={dropdownPage}
                  />
                )}
              />
            </Grid>
          ) : null}
          {watch("taxable") && watch("taxProcedureId")?.value ? (
            <Grid item xs={12} md={4} lg={3}>
              <TextField
                type="number"
                label={t("tax") + " (%)"}
                placeholder={t("tax") + " (%)"}
                title={t("tax") + " (%)"}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={
                  watch("taxProcedureId")?.percentageValue ??
                  taxPercentage * 100 ??
                  0
                }
                disabled={params?.id ? true : false}
              />
            </Grid>
          ) : null}
        </Grid>
        <Grid item xs={12} className="relative stockTransferDetails">
          {loading ? (
            <Loader />
          ) : (
            <>
            <Box className="bg-white py-2 px-[18px] rounded-[8px]">
              <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                {t("supplierBill")}
              </Typography>
              <InventoryItemsTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columns}
                handleInputChange={handleInputChange}
                rows={purchaseInvoiceItem}
                typeName="purchaseInvoiceItem"
                items={itemsData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{
                  params,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                checkStatus={params?.id ? 2 : 1}
                viewDelete={params?.id ? false : true}
                itemsResponse={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary }}
                    disabled={params?.id ? true : false}
                    variant="contained"
                    disableElevation
                    onClick={() => addNewItem("purchaseInvoiceItem")}
                  >
                    <AddIcon className="w-[20px] h-[20px]" />{" "}
                    {t("Add More Items")}
                  </Button>
                  {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Import')}</Button>
                                <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t("Export")}</Button> */}
                </div>
              </Grid>
            </Box>
            <Box className="flex gap-2 justify-start mt-[16px]">
              <Button
                className="secondary cursor-text"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
              >
                {t("Additional Costs")}
              </Button>
            </Box>
            <Box className="bg-white py-2 px-[18px] rounded-[8px] mt-[16px]">
              <Box className="flex justify-between items-center">
                <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                  {t("Additional Cost")}
                </Typography>
                {checkPrivileges("ROLE_SERVICE_CREATE") ? (
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary, padding: "10px" }}
                    variant="contained"
                    disableElevation
                    onClick={() => setAddNew("services")}
                  >
                    <AddIcon className="w-[15px] h-[15px]" />{" "}
                    {t("addNewService")}
                  </Button>
                ) : null}
              </Box>
              <InventoryServicesTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columnsService}
                handleInputChange={handleInputChange}
                rows={purchaseInvoiceService}
                typeName="purchaseInvoiceService"
                services={servicesData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{ params }}
                checkStatus={params?.id ? 2 : 1}
                viewDelete={params?.id ? false : true}
                servicesResponse={servicesData || {}}
                handleDropdownActions={handleDropdownActions}
                {...{
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary }}
                    disabled={params?.id ? true : false}
                    variant="contained"
                    disableElevation
                    onClick={() => addNewItem("purchaseInvoiceService")}
                  >
                    <AddIcon className="w-[20px] h-[20px]" />{" "}
                    {t("Add More Items")}
                  </Button>
                </div>
              </Grid>
              <Grid item xs={12}>
                <Divider className="bg-[#AEB3C4] mt-[15px] mb-[8px]" />
              </Grid>
            </Box>
            </>
          )}
        </Grid>
        {addNew !== "" && (
          <FormDialog
            open={addNew !== ""}
            onClose={(event, reason) =>
              event && reason !== "backdropClick" ? setAddNew("") : ""
            }
            title={t("addService")}
            createPopup={true}
            fullWidth
            hideActions={true}
            content={
              <ServicesModal
                setAddNew={setAddNew}
                serviceRefetch={servicesRefetch}
              />
            }
          />
        )}
        <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left mb-[-5px]">
          {t("Comments")}
        </Typography>
        <Grid container xs={12} spacing={1}>
          <Grid item xs={12} md={6}>
            <TextField
              {...register("comments")}
              placeholder={t("phBillComments")}
              title={t("Comments")}
              fullWidth
              error={!!errors.comments}
              helperText={errors.comments?.message as string}
              InputLabelProps={{ shrink: true }}
              multiline
              rows={4}
              sx={{
                "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                  width: "100%",
                },
              }}
              disabled={params?.id ? true : false}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              className="bg-[#D8D1E0] rounded-[8px]"
              sx={{
                border: "1px solid #5f6770",
                width: "calc(100% + 8px)",
                padding: "8px 14px",
              }}
            >
              <Box className="flex justify-end gap-12 mt-[10px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("Subtotal")}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(
                    totals.subTotal ||
                      (invoice as PurchaseOrderResponse)?.subtotalAmount ||
                      0,
                  )}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[10px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("Discount")}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(
                    !params?.id
                      ? totals.discount
                      : (invoice as PurchaseOrderResponse)?.discount || 0,
                  )}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[10px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                  {t("tax")}
                  {!params?.id && taxPercentage
                    ? ` (${(taxPercentage * 100).toFixed(2)}%)`
                    : ""}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(displayTaxAmount)}
                </Typography>
              </Box>
              <Box className="flex justify-end gap-12 mt-[10px]">
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[auto]">
                  {t("totalWithTax")}
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[20%] text-end">
                  <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                  {new Intl.NumberFormat("en-US", {}).format(
                    displayTotalWithTax,
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} className="hide-on-print">
          <Divider className="bg-[#AEB3C4] mt-[8px]" />
        </Grid>
        <Grid container className="items-center hide-on-print">
          <Grid item xs={12} sm={6} className="text-start"></Grid>
          <Grid item xs={12} sm={6}>
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || loading}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("save")}
                </Button>
              )}
              {params?.id ? (
                <>
                  {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                  <Button
                    className="secondary"
                    variant="contained"
                    style={{ color: themeColors?.primary }}
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                </>
              ) : null}
              <Button
                className="secondary"
                variant="contained"
                style={{ color: themeColors?.primary }}
                disableElevation
                onClick={() => navigate("/inventory/supplier-bills")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PurchaseInvoiceCreateUpdate;
